import { AlertTriangle, Info, Sparkles } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn, navActiveClass } from '@/lib/utils';
import type { BriefingHeaderData } from '../hooks/useBriefingRows';
import { KIND_CONFIG, formatGeneratedAt, titleizeKind } from './briefingShared';

export interface BriefingPageHeaderProps {
  header: BriefingHeaderData;
  /** Active kind filter (the briefing kind scope), null = showing everything. */
  activeKind: string | null;
  /** null clears the filter back to all kinds. */
  onSelectKind: (kind: string | null) => void;
}

/**
 * Self-contained single-row header for the Briefing page: kind filter chips on
 * the left (scrolling within their own track), freshness/stale/notes pinned on
 * the right. The chips drive the existing kind scope, so the filter
 * participates in URL history like the old drill-down rows did.
 */
export function BriefingPageHeader({ header, activeKind, onSelectKind }: BriefingPageHeaderProps) {
  return (
    <div className="flex h-12 shrink-0 items-center gap-3 border-b border-border/60 px-4">
      <div className="scrollbar-none flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto">
        <FilterChip label="All" active={activeKind === null} onClick={() => onSelectKind(null)} />
        {header.kindCounts.map(({ kind, count }) => {
          const config = KIND_CONFIG[kind];
          const Icon = config?.icon ?? Sparkles;
          const colorClass = config?.colorClass ?? 'text-primary';
          const label = config?.label ?? titleizeKind(kind);
          const active = activeKind === kind;
          return (
            <FilterChip
              key={kind}
              label={label}
              count={count}
              icon={<Icon className={cn('h-3.5 w-3.5', active ? undefined : colorClass)} />}
              active={active}
              // Clicking the active chip clears the filter back to All.
              onClick={() => onSelectKind(active ? null : kind)}
            />
          );
        })}
      </div>

      <div className="flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
        {header.isStale ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <AlertTriangle className="h-3.5 w-3.5 text-warning-ink" />
            </TooltipTrigger>
            <TooltipContent>
              Briefing is stale — the scheduled run may have failed. Run /briefing to refresh.
            </TooltipContent>
          </Tooltip>
        ) : null}
        <span className="whitespace-nowrap">{formatGeneratedAt(header.generatedAt)}</span>
        {header.notes.length > 0 ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center transition-colors hover:text-foreground"
                aria-label="Run notes"
              >
                <Info className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-sm">
              {header.notes.map((note, index) => (
                <p key={index} className="my-0.5">
                  {note}
                </p>
              ))}
            </TooltipContent>
          </Tooltip>
        ) : null}
      </div>
    </div>
  );
}

function FilterChip({
  label,
  count,
  icon,
  active,
  onClick,
}: {
  label: string;
  count?: number;
  icon?: React.ReactNode;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
        active
          ? cn(navActiveClass, 'border-primary/30')
          : 'border-border/60 bg-card text-muted-foreground hover:bg-accent/60 hover:text-foreground'
      )}
      aria-pressed={active}
      onClick={onClick}
    >
      {icon}
      {label}
      {typeof count === 'number' ? <span className="opacity-70">{count}</span> : null}
    </button>
  );
}
